/**
 * ExtractoIncompletoBanner — TODO-MDC-22 Fase C.
 *
 * Detecta extractos en estado 'procesando' que quedaron colgados (cliente
 * cayó a mitad del chunking, browser cerrado, network drop, etc.) y los
 * muestra como banner persistente arriba del módulo Finanzas con la
 * opción de descartar.
 *
 * Por qué no ofrecemos "reanudar": el archivo xlsx vive solo en el
 * cliente, no se sube a Storage. Si el cliente murió, el archivo se
 * perdió. Lo correcto es descartar el extracto incompleto + volver a
 * subir el archivo (el dedup del motor evita duplicar las filas que ya
 * se procesaron).
 *
 * Realtime: si otra pestaña termina el procesamiento (estado pasa de
 * 'procesando' a 'parseado'), el banner se actualiza sin polling — el
 * realtime sobre `extractos_bancarios` lo refresca.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/common/Button';
import {
  abandonarExtractoProcesando,
  listExtractosProcesando,
  type ExtractoBancario,
} from '@/services/api/conciliacion';
import { useRealtimeTables } from '@/hooks/useRealtimeTables';

interface Props {
  empresaId: string;
  /** ms desde `procesando_iniciado_at` para considerar el extracto abandonado.
   *  Default: 5 minutos. Antes de eso podría ser una sesión activa en otra pestaña. */
  thresholdMs?: number;
}

const DEFAULT_THRESHOLD_MS = 5 * 60 * 1000;

export function ExtractoIncompletoBanner({
  empresaId,
  thresholdMs = DEFAULT_THRESHOLD_MS,
}: Props) {
  const [extractos, setExtractos] = useState<ExtractoBancario[]>([]);
  const [descartando, setDescartando] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    const res = await listExtractosProcesando(empresaId);
    if (res.ok) setExtractos(res.data);
  }, [empresaId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime: cualquier cambio en extractos_bancarios de esta empresa
  // refresca la lista (un extracto que termina pasa a 'parseado' y
  // desaparece del banner; uno nuevo en 'procesando' aparece).
  useRealtimeTables(
    `extractos-bancarios-${empresaId}`,
    [
      {
        table: 'extractos_bancarios',
        filter: `empresa_id=eq.${empresaId}`,
      },
    ],
    () => {
      void reload();
    },
    !!empresaId,
  );

  // Filtrar: solo mostrar si hace más de thresholdMs que iniciaron.
  // Antes de eso podría ser un proceso vivo en otra pestaña.
  const ahora = Date.now();
  const colgados = extractos.filter((e) => {
    if (!e.procesando_iniciado_at) return false;
    const inicio = new Date(e.procesando_iniciado_at).getTime();
    return ahora - inicio > thresholdMs;
  });

  if (colgados.length === 0) return null;

  async function onDescartar(id: string) {
    setDescartando((prev) => new Set(prev).add(id));
    const res = await abandonarExtractoProcesando(id);
    setDescartando((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (!res.ok) {
      toast.error(res.error.message);
      return;
    }
    if (res.data.borrado) {
      toast.success('Extracto incompleto descartado.');
      void reload();
    } else if (res.data.motivo === 'estado_no_procesando') {
      // Ya no está en 'procesando' — el extracto se completó o fue
      // descartado por otro proceso. Refrescamos.
      void reload();
    }
  }

  return (
    <div className="mb-4 space-y-2">
      {colgados.map((e) => {
        const inicio = e.procesando_iniciado_at
          ? new Date(e.procesando_iniciado_at)
          : null;
        const minsHace = inicio ? Math.floor((ahora - inicio.getTime()) / 60000) : 0;
        const pct =
          e.chunks_total && e.chunks_total > 0
            ? Math.round((e.chunks_procesados / e.chunks_total) * 100)
            : null;

        return (
          <div
            key={e.id}
            className="card-premium bg-orange-10/40 flex items-start gap-3 border-l-4 border-orange p-4"
          >
            <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-orange/15 text-orange">
              <AlertTriangle size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="kicker text-orange">Extracto incompleto</p>
              <h4 className="mt-0.5 text-sm font-bold text-neutral-ink">
                {e.archivo_nombre || 'Extracto sin nombre'}
              </h4>
              <p className="mt-1 text-xs text-neutral-muted">
                Iniciado hace {minsHace} min
                {pct !== null && (
                  <>
                    {' '}
                    · procesó {e.chunks_procesados} de {e.chunks_total} tandas ({pct}%)
                  </>
                )}
                . El procesamiento se cortó antes de terminar — descartá el parcial y
                volvé a subir el archivo. El sistema evita duplicar filas ya integradas.
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => onDescartar(e.id)}
              disabled={descartando.has(e.id)}
              leftIcon={
                descartando.has(e.id) ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <X size={14} />
                )
              }
            >
              Descartar
            </Button>
          </div>
        );
      })}
    </div>
  );
}
